import Drawer from '@/components/Drawer';
import { F, getIntlTranslation } from '@/utils/intl';
import {
  BgColorsOutlined,
  BoldOutlined,
  CompressOutlined,
  ExpandOutlined,
  FontColorsOutlined,
  FontSizeOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  TranslationOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Button,
  ColorPicker,
  Dropdown,
  Input,
  Select,
  Space,
  Tooltip,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'umi';
import TranslationKeyOption from '../TranslationKeyOption';
import { useTranslationEntries } from '../useTranslationEntries';
import { useVariableSuggestions } from '../useVariableSuggestions';
import VariableInsertButton from '../VariableInsertButton';
import VariableSuggestionPopup, {
  SuggestionAnchor,
} from '../VariableSuggestionPopup';
import { EMAIL_SAFE_FONTS, matchFontStack } from './emailFonts';
import {
  EmailLink,
  InlineTextStyle,
  TranslationAtom,
  VariableAtom,
} from './richTextExtensions';
import { fromEditorHtml, toEditorHtml } from './richTextValue';
import { EmailVariableGroup } from './types';

import styles from './RichTextField.less';

/**
 * The sizes offered, as a ramp rather than a number field.
 *
 * Same reasoning as `numberPresets` for the inspector: an email is a design, and a
 * free number box is how one ends up with nine slightly different body sizes. The
 * empty option is "inherit", which is what most runs should be — it clears the mark
 * instead of pinning a size the block's own styling would otherwise provide.
 */
const TEXT_SIZES = [
  { value: '', label: 'Default' },
  { value: '12px', label: 'S' },
  { value: '14px', label: 'M' },
  { value: '16px', label: 'L' },
  { value: '20px', label: 'XL' },
  { value: '28px', label: 'XXL' },
];

interface RichTextFieldProps {
  /** The stored prop value: HTML with Twig expressions in it. */
  value: string;
  onChange: (next: string) => void;
  /** Which language the translation dropdown and the `{{` list show wordings for. */
  language: string;
  variableGroups?: EmailVariableGroup[];
  disabled?: boolean;
  placeholder?: string;
  /** Ends the shell's undo run, so a visit is one step rather than one per keystroke. */
  onEndEditRun?: () => void;
}

/**
 * WYSIWYG editing for one text prop, with variables and translations as chips.
 *
 * ### What it will and will not open
 *
 * The caller decides, with `canEditRichly` — this component assumes the value is
 * representable. That split matters: the gate is what stops a WYSIWYG from flattening
 * a partner's hand-written markup, and it is pure and tested, while this file is the
 * widget layer.
 *
 * ### Undo
 *
 * `StarterKit` brings ProseMirror's own history, which is what should handle ⌘Z while
 * the caret is in here — the shell's `ownsItsOwnFocus` already keeps the document-level
 * shortcut off a contenteditable. `onEndEditRun` fires on blur so the *document's*
 * history gets one step per visit.
 *
 * ### The sync effect
 *
 * It compares the incoming value against `fromEditorHtml(editor.getHTML())` — the
 * value's own form — not against the editor's HTML. Comparing the two forms directly
 * would differ on every render (atoms vs Twig, wrapped vs unwrapped paragraph), so
 * `setContent` would fire mid-word and throw the caret to the end.
 */
const RichTextField: React.FC<RichTextFieldProps> = ({
  value,
  onChange,
  language,
  variableGroups,
  disabled,
  placeholder,
  onEndEditRun,
}) => {
  const intl = useIntl();
  /** Every saved text with its wording for this language — the `{{` list and the
      Text dropdown offer the same rows, so a pick is informed either way. */
  const translationEntries = useTranslationEntries(language);
  const [linkOpen, setLinkOpen] = useState(false);
  /** Whether the editor is mounted in the side drawer instead of the panel. */
  const [expanded, setExpanded] = useState(false);
  const [linkDraft, setLinkDraft] = useState('');

  /**
   * The live editor, for the callbacks that reach it from outside React's render —
   * the suggestion hook and ProseMirror's own key handler, both built once.
   */
  const editorRef = useRef<ReturnType<typeof useEditor>>(null);
  /** Caret coordinates for the suggestion list, in viewport space. */
  const [anchor, setAnchor] = useState<SuggestionAnchor | null>(null);
  const keyRef = useRef<(key: string) => boolean>(() => false);
  const syncRef = useRef<() => void>(() => {});
  const closeRef = useRef<() => void>(() => {});

  const editor = useEditor(
    {
      editorProps: {
        handleKeyDown: (_view, event) => {
          if (!keyRef.current(event.key)) return false;
          event.preventDefault();
          // The expanded editor lives in a drawer that closes on Escape;
          // dismissing a suggestion list is not asking for that.
          event.stopPropagation();
          return true;
        },
      },
      extensions: [
        // Only what email copy needs. `heading` off: `core.heading` is its own block,
        // and a heading inside a text block would fight it. `codeBlock`/`blockquote`
        // off: they are not email copy, and each one is another tag the round-trip
        // gate would have to admit.
        StarterKit.configure({
          heading: false,
          codeBlock: false,
          blockquote: false,
          horizontalRule: false,
          code: false,
          strike: false,
        }),
        EmailLink,
        InlineTextStyle,
        VariableAtom,
        TranslationAtom,
      ],
      content: toEditorHtml(value),
      editable: !disabled,
      onUpdate({ editor: instance }) {
        onChange(fromEditorHtml(instance.getHTML()));
        syncRef.current();
      },
      onSelectionUpdate() {
        syncRef.current();
      },
      onBlur() {
        closeRef.current();
        onEndEditRun?.();
      },
    },
    // Recreated when editability changes; content changes go through the effect below.
    [disabled],
  );

  useEffect(() => {
    if (!editor) return;
    // Compare in the *value's* form — see the note above on why.
    if (fromEditorHtml(editor.getHTML()) === value) return;
    editor.commands.setContent(toEditorHtml(value), false);
  }, [value, editor]);

  editorRef.current = editor;

  const insertVariable = useCallback(
    (path: string) => {
      editor
        ?.chain()
        .focus()
        .insertContent({ type: 'cvnVariable', attrs: { value: path } })
        .run();
    },
    [editor],
  );

  /**
   * The same `{{` autocomplete as the fields and the source view. A pick replaces
   * the half-typed braces with a **chip**, not with text: in here a variable is an
   * atom, and leaving `{{ … }}` as characters would be a thing the user could then
   * half-delete.
   */
  const variables = useVariableSuggestions({
    variableGroups,
    translations: translationEntries,
    enabled: !disabled,
    onAccept: (option, trigger) => {
      editorRef.current
        ?.chain()
        .focus()
        .deleteRange({ from: trigger.start, to: trigger.end })
        .insertContent({
          // A saved text is its own atom, with its own chip — inserting the
          // `|trans` Twig as characters would render as code in the canvas.
          type:
            option.kind === 'translation' ? 'cvnTranslation' : 'cvnVariable',
          attrs: { value: option.path },
        })
        .run();
    },
  });

  /**
   * Reports the caret. Offsets from `textBetween` line up with document positions
   * because every chip counts as the one character its placeholder stands for —
   * which is what lets the hook hand back a range this editor can delete.
   */
  const syncSuggestions = useCallback(() => {
    const instance = editorRef.current;
    if (!instance) return;
    const { state } = instance;
    const { from } = state.selection;
    const blockStart = state.doc.resolve(from).start();
    variables.sync(
      state.doc.textBetween(blockStart, from, '\n', '\ufffc'),
      blockStart,
    );

    // Handed over as the caret's viewport box: the list is portalled to the body,
    // so it does the offsetting and the edge-clamping itself.
    const coords = instance.view.coordsAtPos(from);
    if (!coords) return;
    setAnchor({
      left: coords.left,
      top: coords.top,
      bottom: coords.bottom,
    });
  }, [variables]);

  syncRef.current = syncSuggestions;
  keyRef.current = variables.handleKey;
  closeRef.current = variables.close;

  /**
   * The list is fixed-positioned, so anything that moves the caret on screen without
   * editing the text — the inspector rail scrolling under it, the window resizing —
   * has to re-measure or the card detaches from the word it belongs to. Capture
   * phase: the scroll happens on an ancestor container and does not bubble.
   */
  useEffect(() => {
    if (!variables.open) return undefined;
    const reanchor = () => syncRef.current();
    window.addEventListener('scroll', reanchor, true);
    window.addEventListener('resize', reanchor);
    return () => {
      window.removeEventListener('scroll', reanchor, true);
      window.removeEventListener('resize', reanchor);
    };
  }, [variables.open]);

  const insertTranslation = useCallback(
    (key: string) => {
      editor
        ?.chain()
        .focus()
        .insertContent({ type: 'cvnTranslation', attrs: { value: key } })
        .run();
    },
    [editor],
  );

  /** The same rows the `{{` list offers, as the Text dropdown's menu. */
  const translationItems = useMemo(
    () =>
      translationEntries.map(({ key, text }) => ({
        key,
        label: <TranslationKeyOption translationKey={key} text={text} />,
      })),
    [translationEntries],
  );

  /**
   * The dropdown's rows. Family names are proper nouns — only the "inherit" row is
   * a word, and clearing to it is what lets a run go back to the block's own font.
   */
  const fontOptions = useMemo(
    () => [
      {
        value: '',
        label: getIntlTranslation(intl, {
          id: 'pages.emailCenter.richText.fontDefault',
          defaultMessage: 'Default',
        }),
      },
      ...EMAIL_SAFE_FONTS.map((font) => ({
        value: font.stack,
        label: font.label,
      })),
    ],
    [intl],
  );

  const applyLink = useCallback(() => {
    if (!editor) return;
    const href = linkDraft.trim();
    if (href) {
      editor.chain().focus().setEmailLink({ href }).run();
    } else {
      editor.chain().focus().unsetEmailLink().run();
    }
    setLinkOpen(false);
  }, [editor, linkDraft]);

  if (!editor) return null;

  const active = (name: string, attrs?: Record<string, unknown>) =>
    editor.isActive(name, attrs) ? styles.on : undefined;

  /**
   * `InlineTextStyle`'s commands, typed.
   *
   * TipTap v2 grows its command types from a module augmentation the v3 extension
   * packages provide and this hand-written v2 mark does not, so the chain has no
   * idea these exist. Named here rather than casting at each call site, so the
   * shape is stated once.
   */
  const styleChain = () =>
    editor.chain().focus() as unknown as {
      setTextSize: (size: string | null) => { run: () => void };
      setTextColor: (colour: string | null) => { run: () => void };
      setTextFont: (stack: string | null) => { run: () => void };
    };

  /**
   * The font at the caret, and the rows to show it among.
   *
   * A partner's own `font-family` passes the gate, so a stack that is not one of
   * ours is offered as itself rather than displayed as *Default* — a control that
   * misreports the value it is editing is worse than one with a stray row.
   */
  const activeFont: string | null =
    editor.getAttributes('cvnTextStyle').fontFamily ?? null;
  const listedFont = matchFontStack(activeFont);
  const fontSelectOptions =
    activeFont && !listedFont
      ? [...fontOptions, { value: activeFont, label: activeFont }]
      : fontOptions;

  /* Rendered into whichever surface is mounted — the panel or the drawer. */
  const suggestionPopup = (
    <VariableSuggestionPopup
      anchor={variables.open ? anchor : null}
      suggestions={variables.suggestions}
      activeIndex={variables.activeIndex}
      onPick={variables.accept}
    />
  );

  return (
    <div className={styles.field} data-disabled={disabled ? '1' : undefined}>
      <div className={styles.toolbar}>
        <Space size={2}>
          <Tooltip
            title={getIntlTranslation(intl, {
              id: 'pages.emailCenter.richText.bold',
              defaultMessage: 'Bold',
            })}
          >
            <Button
              size="small"
              type="text"
              className={active('bold')}
              disabled={disabled}
              icon={<BoldOutlined />}
              onClick={() => editor.chain().focus().toggleBold().run()}
            />
          </Tooltip>
          <Tooltip
            title={getIntlTranslation(intl, {
              id: 'pages.emailCenter.richText.italic',
              defaultMessage: 'Italic',
            })}
          >
            <Button
              size="small"
              type="text"
              className={active('italic')}
              disabled={disabled}
              icon={<ItalicOutlined />}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />
          </Tooltip>

          <Tooltip
            title={
              expanded
                ? getIntlTranslation(intl, {
                    id: 'pages.emailCenter.richText.collapse',
                    defaultMessage: 'Back to the panel',
                  })
                : getIntlTranslation(intl, {
                    id: 'pages.emailCenter.richText.expand',
                    defaultMessage: 'Write in a larger editor',
                  })
            }
          >
            <Button
              size="small"
              type="text"
              icon={expanded ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={() => setExpanded((open) => !open)}
            />
          </Tooltip>

          {/* Only fonts every mail client has: there is no webfont in Outlook, so
              anything else is silently swapped for the client's default. */}
          <Tooltip
            title={getIntlTranslation(intl, {
              id: 'pages.emailCenter.richText.font',
              defaultMessage: 'Font',
            })}
          >
            <Select
              size="small"
              variant="borderless"
              className={styles.fontSelect}
              disabled={disabled}
              value={listedFont ?? activeFont ?? ''}
              onChange={(next) =>
                styleChain()
                  .setTextFont(next || null)
                  .run()
              }
              suffixIcon={<FontColorsOutlined />}
              options={fontSelectOptions}
            />
          </Tooltip>

          {/* Size as a named ramp, not a number box: these values land in a real
              email, where arbitrary sizes are how a design stops being one. */}
          <Tooltip
            title={getIntlTranslation(intl, {
              id: 'pages.emailCenter.richText.size',
              defaultMessage: 'Text size',
            })}
          >
            <Select
              size="small"
              variant="borderless"
              className={styles.sizeSelect}
              disabled={disabled}
              value={editor.getAttributes('cvnTextStyle').fontSize ?? ''}
              onChange={(next) =>
                styleChain()
                  .setTextSize(next || null)
                  .run()
              }
              suffixIcon={<FontSizeOutlined />}
              options={TEXT_SIZES}
            />
          </Tooltip>

          {/* Cleared by picking nothing, so a run can go back to inheriting the
              block's colour rather than being pinned to whatever was chosen once. */}
          <ColorPicker
            size="small"
            disabled={disabled}
            allowClear
            value={editor.getAttributes('cvnTextStyle').color ?? null}
            onChangeComplete={(colour) =>
              styleChain()
                .setTextColor(colour?.toHexString() ?? null)
                .run()
            }
            onClear={() => styleChain().setTextColor(null).run()}
          >
            <Tooltip
              title={getIntlTranslation(intl, {
                id: 'pages.emailCenter.richText.colour',
                defaultMessage: 'Text colour',
              })}
            >
              <Button
                size="small"
                type="text"
                disabled={disabled}
                icon={<BgColorsOutlined />}
              />
            </Tooltip>
          </ColorPicker>

          {/* A link needs a URL, so the button opens a small field rather than
              toggling something the user cannot see. */}
          <Dropdown
            open={linkOpen}
            onOpenChange={(next) => {
              setLinkOpen(next);
              if (next) setLinkDraft(editor.getAttributes('link').href ?? '');
            }}
            trigger={['click']}
            disabled={disabled}
            popupRender={() => (
              <div className={styles.linkPopover}>
                <Input
                  size="small"
                  autoFocus
                  value={linkDraft}
                  placeholder="https://…"
                  onChange={(event) => setLinkDraft(event.target.value)}
                  onPressEnter={applyLink}
                />
                <Space size={4}>
                  <Button size="small" type="primary" onClick={applyLink}>
                    <F id="common.actions.apply" defaultMessage="Apply" />
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setLinkDraft('');
                      applyLink();
                    }}
                  >
                    <F
                      id="pages.emailCenter.richText.removeLink"
                      defaultMessage="Remove"
                    />
                  </Button>
                </Space>
              </div>
            )}
          >
            <Button
              size="small"
              type="text"
              className={active('link')}
              disabled={disabled}
              icon={<LinkOutlined />}
            />
          </Dropdown>

          <Tooltip
            title={getIntlTranslation(intl, {
              id: 'pages.emailCenter.richText.bulletList',
              defaultMessage: 'Bulleted list',
            })}
          >
            <Button
              size="small"
              type="text"
              className={active('bulletList')}
              disabled={disabled}
              icon={<UnorderedListOutlined />}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            />
          </Tooltip>
          <Tooltip
            title={getIntlTranslation(intl, {
              id: 'pages.emailCenter.richText.orderedList',
              defaultMessage: 'Numbered list',
            })}
          >
            <Button
              size="small"
              type="text"
              className={active('orderedList')}
              disabled={disabled}
              icon={<OrderedListOutlined />}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            />
          </Tooltip>
        </Space>

        <Space size={4}>
          <VariableInsertButton
            variableGroups={variableGroups}
            disabled={disabled}
            onInsert={(namespace, name) =>
              insertVariable(`${namespace}.${name}`)
            }
            label={
              <F
                id="pages.emailCenter.richText.variable"
                defaultMessage="Variable"
              />
            }
          />
          <Dropdown
            trigger={['click']}
            disabled={disabled || translationItems.length === 0}
            menu={{
              items: translationItems,
              className: styles.menu,
              onClick: ({ key }) => insertTranslation(String(key)),
            }}
          >
            <Button
              size="small"
              icon={<TranslationOutlined />}
              disabled={disabled || translationItems.length === 0}
            >
              <F
                id="pages.emailCenter.richText.translation"
                defaultMessage="Text"
              />
            </Button>
          </Dropdown>
        </Space>
      </div>

      {/**
       * One editor, moved — not a second one opened on the same value.
       *
       * `EditorContent` is a mount point for the existing instance, so rendering it
       * inside the drawer relocates the live editor: the selection, the undo stack
       * and the in-flight marks all survive the trip, and there is no second
       * instance whose content could disagree with this one's.
       */}
      {expanded ? (
        <Drawer
          open
          width="calc(100vw - 200px)"
          title={
            <F
              id="pages.emailCenter.richText.expandedTitle"
              defaultMessage="Editing text"
            />
          }
          onOpenChange={() => setExpanded(false)}
          onClose={() => setExpanded(false)}
        >
          <div className={styles.surface}>
            <EditorContent
              className={`${styles.content} ${styles.contentExpanded}`}
              editor={editor}
              data-placeholder={placeholder}
            />
            {suggestionPopup}
          </div>
        </Drawer>
      ) : (
        <div className={styles.surface}>
          <EditorContent
            className={styles.content}
            editor={editor}
            data-placeholder={placeholder}
          />
          {suggestionPopup}
        </div>
      )}
    </div>
  );
};

export default React.memo(RichTextField);
